import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from "@angular/core";
import { Icon } from "../../common/components/general/icon/icon";
import { MatMenuItem, MatMenuModule } from "@angular/material/menu";
import { ReusableService } from "../../common/services/reusable-services";
import { Router } from "@angular/router";
import { Label } from "../../common/models/page-components";
import { Observable } from "rxjs";
import { COMPONENT_META_DATA } from "../../common/constants/component-meta-data";
import { HeaderService } from "../header.service";
import { ContentFilterPipe } from "../../common/pipes/content-filter-pipe";
import { ChildMenu, HeaderResults } from "../header-model";
import { ApiResponse } from "../../common/models/api-response.model";
import { UserDetailsPartial } from "./user-profile.model";
import { CacheService } from "../../common/services/cache-service";
import { CACHE_KEYS } from "../../common/constants/cache-keys";
import { MenuService } from "../menu/menu-service";
import { LogoutService } from "../../common/services/logout-service";
import { GigyaSessionService } from "../../common/services/gigya-session-service";
import { environment } from "../../../environments/environment";
import { SRP_ROUTES } from "../../common/constants/routes";
import { AnalyticsService } from "../../common/services/analytics-service";
import { LoadingService } from "../../common/components/general/loading/loading-service";

export interface Options {
  option: string;
  optionUrl: string;
}

@Component({
  selector: "srp-user-profile",
  imports: [Icon, MatMenuModule, MatMenuItem, ContentFilterPipe],
  templateUrl: "./user-profile.html",
  styleUrl: "./user-profile.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfile implements OnInit {
  @Input() pageContentLabels: Label[];
  @Input() appContext: string;

  labels: Label[];
  header$!: Observable<ApiResponse<HeaderResults>>;
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;
  userDetails: UserDetailsPartial;
  userNickname: string;
  adminLinks: ChildMenu[] = [];
  poiLink: string;
  tiLink: string;
  isFromUserMenu = false;

  readonly menuService = inject(MenuService);
  readonly logoutService = inject(LogoutService);
  readonly gigyaSessionService = inject(GigyaSessionService);
  readonly analyticsService = inject(AnalyticsService);

  constructor(
    private readonly reusableService: ReusableService,
    private readonly router: Router,
    public readonly headerService: HeaderService,
    private readonly cacheService: CacheService,
    private readonly loadingService: LoadingService,
  ) { }

  ngOnInit() {
    this.poiLink = environment.POI;
    this.tiLink = environment.TI;
    this.userDetails = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_DETAILS);
    this.userNickname = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_NICKNAME);
    this.headerService.getHeaderResponseFromObservable().subscribe((response) => {
      if (response?.data) {
        this.labels = response.data.labels;
        if (response.data.userMenuLinks?.length) {
          this.isFromUserMenu = true;
          this.adminLinks = response.data.userMenuLinks;
        } else {
          this.adminLinks = response.data.appLinks.parentMenu.filter(menu => menu.label.trim().toLocaleLowerCase() === 'administration')[0]?.childMenu;
        }
      }
    });
  }

  navigate(menu: ChildMenu, configLink?: string): void {
    this.analyticsService.trackClickEvent(
      'uaevent',
      'Header',
      'Redirection::External',
      menu.link === this.poiLink ? 'POI Link' : 'TI Link'
    );
    this.reusableService.navigateToUrl(menu, this.appContext, menu.label, { useAngularRouter: menu.category == 'internal' });
  }

  externalLinkNavigation(link: string) {
    this.analyticsService.trackClickEvent(
      'uaevent',
      'Header',
      'Redirection::External',
      link === this.poiLink ? 'POI Link' : 'TI Link'
    );
    window.open(link, '_blank'); // '_blank' opens in a new tab 
  }

  async logout() {
    this.loadingService.start();

    try {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.FORCE_LOGIN, true);
      this.gigyaSessionService.stopMonitoring();
      await this.logoutService.logout();
      this.reusableService.navigateToUrl(`/${SRP_ROUTES.LOGIN.PATH}`, this.appContext, 'login');
    } catch (error) {
      console.log("Logout Error: ", error);
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.FORCE_LOGIN, true);
      this.reusableService.navigateToUrl(`/${SRP_ROUTES.LOGIN.PATH}`, this.appContext, 'login');
    } finally {
      this.loadingService.stop();
    }
  }
}
